import { Button, DialogActions, DialogTitle } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
    IWorkspacePermission,
    WorkspacePermissionObjectType
} from "common/interfaces";
import WorkspaceController, {
    WorkspaceControllerState,
    getWorkspacePermissions
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import { UserProfile } from "js/react/components/Avatar";
import {
    BeautifulDialog,
    DialogContent
} from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";

const logger = getLogger();

const FlexDiv = styled.div`
    display: flex;
`;

const SpacedPTag = styled.p`
    letter-spacing: .1px;
`;

const ProfileContainer = styled.div`
    display: flex;
    padding: 10px;
`;

const OwnersAndLibrariansProfilesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    overflow-y: auto;
`;

const StyledDialogTitle = styled(DialogTitle)`
    &&& {
        padding: 34px 34px 0;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &&& {
        padding: 0 34px 0;
    }
`;

export interface MissingManageTeamFolderPermissionsDialogProps {
    folderId: string;
    closeDialog: () => void;
}

export const MissingManageTeamFolderPermissionsDialog = WorkspaceController.withInitializedState(
    function MissingManageTeamFolderPermissionsDialog(props: MissingManageTeamFolderPermissionsDialogProps & WorkspaceControllerState) {
        const { closeDialog, users, resourceFolders, folderId } = props;

        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }

        const [fetching, setFetching] = useState(false);
        const [permissions, setPermissions] = useState<IWorkspacePermission[]>([]);

        useEffect(() => {
            (async () => {
                setFetching(true);
                try {
                    const permissions = await getWorkspacePermissions({
                        workspaceId: WorkspaceController.workspaceId,
                        object: {
                            type: WorkspacePermissionObjectType.RESOURCE_FOLDER,
                            id: folderId
                        }
                    });
                    setPermissions(permissions);
                    setFetching(false);
                } catch (err) {
                    logger.error(err, "[MissingManageTeamFolderPermissionsDialog] Failed to fetch folder permissions");
                }
            })();
        }, [folderId]);

        const owners = users.filter(user =>
            permissions.some(permission => permission.subjectId === user.id && permission.owner) ||
            permissions.some(permission => user.groupIds.includes(permission.subjectId) && permission.owner)
        );

        return (
            <BeautifulDialog closeDialog={closeDialog}>
                <FetchingClickShield visible={fetching} backgroundColor="white" />
                <StyledDialogTitle>Sorry, you don't have permission to manage folder access.</StyledDialogTitle>
                <StyledDialogContent>
                    <SpacedPTag>This folder's settings only allow folder owners to update folder access. You can reach out to the following team members for help:</SpacedPTag>
                    <FlexDiv>
                        <OwnersAndLibrariansProfilesContainer>
                            {owners.map(user => {
                                return (
                                    <ProfileContainer key={user.uid}>
                                        <UserProfile profile={user} />
                                    </ProfileContainer>
                                );
                            })}
                        </OwnersAndLibrariansProfilesContainer>
                    </FlexDiv>
                </StyledDialogContent>
                <DialogActions>
                    <Button color="primary" onClick={closeDialog} >
                        Got It
                    </Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
);
